import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Home, ListChecks, User, Plus, Truck, Bell, FileCheck, Wallet, Users, Route, Receipt, AlertTriangle } from "lucide-react";
import { GlobeBg, Orbit, Wordmark } from "@/pages/preview/LogoMotifs";
import { KuwaitPlate } from "@/components/KuwaitPlate";

const ANIMS = {
  slide: { name: "انزلاق جانبي", initial: { x: -60, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: 60, opacity: 0 } },
  zoom: { name: "تلاشي مع تكبير", initial: { scale: 0.94, opacity: 0 }, animate: { scale: 1, opacity: 1 }, exit: { scale: 1.04, opacity: 0 } },
  orbit: { name: "دوران مداري (من الشعار)", initial: { rotate: -6, x: -80, y: 30, opacity: 0 }, animate: { rotate: 0, x: 0, y: 0, opacity: 1 }, exit: { rotate: 6, x: 80, y: -20, opacity: 0 } },
};
const BRAND = "linear-gradient(160deg, #013B79 0%, #0B5CB8 100%)";

function HomeScreen() {
  return (
    <div>
      <div className="relative text-white px-4 pt-4 pb-20 overflow-hidden" style={{ background: BRAND }}>
        <GlobeBg className="absolute -left-16 -top-10 w-72 h-72" />
        <Orbit className="absolute left-0 top-40 w-full" />
        <div className="relative flex items-center justify-between">
          <img src="/img/logo.png" alt="" className="w-14 h-14 rounded-full bg-white p-0.5 object-contain ring-2 ring-white/50" />
          <Wordmark color="white" className="text-xl" />
          <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center"><Bell className="w-5 h-5" /></span>
        </div>
        <div className="relative mt-6"><div className="text-white/80 text-sm">مرحباً</div><div className="text-3xl font-black">محمد</div></div>
        <div className="relative mt-4 flex items-center justify-between rounded-3xl bg-white/12 backdrop-blur border border-white/20 p-3" style={{ background: "rgba(255,255,255,0.12)" }}>
          <KuwaitPlate adm="20" plate="45454" /><Truck className="w-12 h-12" />
        </div>
      </div>
      <div className="-mt-12 px-4 relative space-y-3">
        <div className="rounded-3xl bg-white shadow-xl p-4 flex items-center justify-between text-[#013B79]">
          <div><div className="text-2xl font-black">نقلة جديدة</div><div className="text-xs text-slate-500">ابدأ نقلة من هنا</div></div>
          <span className="w-14 h-14 rounded-full flex items-center justify-center text-white" style={{ background: BRAND }}><Plus className="w-8 h-8" strokeWidth={3} /></span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[[Truck, "نقلاتي"], [Bell, "إشعاراتي"], [FileCheck, "مستنداتي"], [Wallet, "حسابي"]].map(([I, l]) => (
            <div key={l} className="bg-white rounded-2xl p-3 flex flex-col items-center gap-2 shadow-sm">
              <span className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ background: BRAND }}><I className="w-6 h-6" /></span><b className="text-sm text-slate-800">{l}</b>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ListScreen({ title }) {
  return (
    <div>
      <div className="relative text-white px-4 py-5 overflow-hidden" style={{ background: BRAND }}>
        <GlobeBg className="absolute -left-10 -top-16 w-48 h-48" /><h2 className="relative text-2xl font-black">{title}</h2>
      </div>
      <div className="p-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 border-r-4 border-[#013B79]">
            <span className="w-11 h-11 rounded-xl bg-[#E6EEF8] text-[#013B79] flex items-center justify-center"><Truck className="w-5 h-5" /></span>
            <div className="flex-1"><b className="block">TRP-00031{i}</b><span className="text-xs text-slate-500">الجهراء ← الشويخ</span></div><b className="text-[#013B79]">25.000</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhoneDemo({ anim }) {
  const [tab, setTab] = useState("home");
  const a = ANIMS[anim];
  const tabs = [["account", "حسابي", User], ["home", "الرئيسية", Home], ["trips", "نقلاتي", ListChecks]];
  return (
    <div className="w-[370px] max-w-full mx-auto h-[720px] rounded-[2.6rem] border-[10px] border-black overflow-hidden shadow-2xl relative bg-[#F2F6FB]" style={{ fontFamily: "'Almarai', sans-serif" }} data-testid="logo-design-phone">
      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={a.initial} animate={a.animate} exit={a.exit} transition={{ duration: 0.35, ease: "easeOut" }} className="absolute inset-0 overflow-y-auto pb-24">
          {tab === "home" ? <HomeScreen /> : <ListScreen title={tab === "trips" ? "نقلاتي" : "حسابي"} />}
        </motion.div>
      </AnimatePresence>
      <div className="absolute bottom-0 inset-x-0 h-[70px] flex items-center justify-around text-white rounded-t-[1.6rem]" style={{ background: BRAND }}>
        {tabs.map(([k, l, I]) => (
          <button key={k} data-testid={`logo-tab-${k}`} onClick={() => setTab(k)} className={`flex flex-col items-center gap-1 text-[11px] ${tab === k ? "font-black" : "opacity-60"}`}>
            <motion.span animate={{ scale: tab === k ? 1.2 : 1, y: tab === k ? -3 : 0 }}><I className="w-6 h-6" /></motion.span>{l}
          </button>
        ))}
      </div>
    </div>
  );
}

function AdminStrip() {
  return (
    <div className="rounded-3xl overflow-hidden shadow-xl bg-white" style={{ fontFamily: "'Almarai', sans-serif" }} data-testid="logo-design-admin">
      <div className="relative text-white p-6 overflow-hidden" style={{ background: BRAND }}>
        <GlobeBg className="absolute -left-20 -top-24 w-96 h-96" opacity={0.15} /><Orbit className="absolute left-0 bottom-0 w-2/3" />
        <div className="relative flex items-center gap-4"><img src="/img/logo.png" alt="" className="w-20 h-20 rounded-full bg-white p-1 object-contain" />
          <div><h2 className="text-2xl font-black">لوحة التحكم</h2><Wordmark color="white" className="text-lg justify-start" /></div></div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4">
        {[[Users, "إجمالي السائقين", "24"], [Route, "نقلات اليوم", "13"], [Receipt, "المحصل", "4,250.000"], [AlertTriangle, "المخالفات", "7"]].map(([I, l, v]) => (
          <div key={l} className="rounded-2xl p-4 border border-[#013B79]/10 bg-[#F2F6FB] flex items-center gap-3">
            <span className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ background: BRAND }}><I className="w-6 h-6" /></span>
            <div><div className="text-xs text-slate-500 font-bold">{l}</div><div className="text-xl font-black text-[#013B79]">{v}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DesignLogo() {
  const [anim, setAnim] = useState("orbit");
  return (
    <div className="min-h-screen bg-slate-200 p-4 lg:p-8 space-y-5" data-testid="design-logo-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-black">عينة تصميم مستوحى من الشعار</h1><p className="text-slate-600 text-sm">الكرة الأرضية والمدار والشاحنة وكلمة A.O.T.C — اضغط أزرار الشريط السفلي في الهاتف لتشاهد حركة الانتقال</p></div>
        <Link to="/admin" data-testid="logo-sample-back" className="btn btn-sm btn-ghost"><ArrowRight className="w-4 h-4" />العودة للنظام</Link>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="anim-picker">
        <b className="text-sm self-center">حركة الانتقال بين الصفحات:</b>
        {Object.entries(ANIMS).map(([k, v], i) => (
          <button key={k} data-testid={`anim-${k}`} onClick={() => setAnim(k)} className={`btn btn-sm ${anim === k ? "btn-blue" : "btn-ghost"}`}>الحركة {i + 1}: {v.name}</button>
        ))}
      </div>
      <div className="grid xl:grid-cols-[400px_1fr] gap-6 items-start">
        <PhoneDemo key={anim} anim={anim} />
        <AdminStrip />
      </div>
    </div>
  );
}
